import { useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { CallGraphApiResponse } from '@/components/smartai/api/getCallGraph';
import type { CompareResponse } from '@/components/smartai/api/compareBuildDiff';
import {
  flattenCallables,
  mapTestOptimizationMethodsToRows,
  filterMethods,
  countByFilterWithDiffSummary,
  fanInKeys,
  buildMethodDevRefCsv,
  isMethodRowSavable,
  type FilterKey,
  type MethodRow,
} from './testOptimizationUtils';
import { downloadCsvContent, sanitizeFileNamePart } from '@/utils/downloadJson';
import SaveTestRunSpecModal from './SaveTestRunSpecModal';
import { cn } from '@/lib/utils';
import {
  testOptChangeClass,
  testOptColChangeClass,
  testOptColCheckClass,
  testOptColCoveredClass,
  testOptColFanClass,
  testOptColFileClass,
  testOptColMethodClass,
  testOptColScoreClass,
  testOptColSeverityClass,
  testOptCoveredChipClass,
  testOptCoveredEmptyClass,
  testOptCoveredListClass,
  testOptDetailBodyClass,
  testOptDetailChangeClass,
  testOptDetailHeaderClass,
  testOptDetailMetricsClass,
  testOptDetailNameClass,
  testOptDetailPathClass,
  testOptDetailClass,
  testOptDetailSubtab,
  testOptDetailSubtabsClass,
  testOptDetailTitleClass,
  testOptFilterClassName,
  testOptFilterCountBadge,
  testOptFooterActionsClass,
  testOptFooterClass,
  testOptFooterSummaryClass,
  testOptMethodsClass,
  testOptMethodsEmbeddedClass,
  testOptMethodsFilterGroupClass,
  testOptMethodsMainClass,
  testOptMethodsSearchClass,
  testOptMethodsSearchIconClass,
  testOptMethodsSearchWrapClass,
  testOptMethodsSourceHintClass,
  testOptMethodsToolbarFiltersClass,
  testOptMethodsTableWrapClass,
  testOptMethodsToolbarClass,
  testOptPlaceholderClass,
  testOptPlaceholderErrorClass,
  testOptSeverityClass,
  testOptTableClass,
  testOptTableCoveredClass,
  testOptTableFileClass,
  testOptTableFileNameClass,
  testOptTableFilePathClass,
  testOptTableMethodClass,
  testOptTableMethodLineClass,
  testOptTableMethodTypeClass,
  testOptTableRowClass,
  testOptTdCheckClass,
  testOptTdClass,
  testOptTdNumClass,
  testOptThCheckClass,
  testOptThClass,
  testOptThNumClass,
} from './test-optimization-layout';

type Props = {
  payload: CallGraphApiResponse | undefined;
  loading: boolean;
  errorMessage?: string;
  embedded?: boolean;
  scanId?: string;
  /** Build Diff compare payload (uses testOptimizationMethods + scanB from API). */
  diffData?: CompareResponse | null;
  onSpecSaved?: () => void;
};

const MethodsTab = ({
  payload,
  loading,
  errorMessage,
  embedded,
  scanId = '',
  diffData,
  onSpecSaved,
}: Props) => {
  const [filter, setFilter] = useState<FilterKey>('all');
  const [search, setSearch] = useState('');
  const [focusedKey, setFocusedKey] = useState<string | null>(null);
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [detailTab, setDetailTab] = useState<'tests' | 'callers'>('tests');
  const [saveSpecOpen, setSaveSpecOpen] = useState(false);
  const [saveSuccessMessage, setSaveSuccessMessage] = useState<string | null>(
    null,
  );

  /** Build Diff embed: only ADO diff + scanEntities rows — never full call-graph. */
  const isBuildDiffMethods = Boolean(embedded && diffData);

  const allRows = useMemo(() => {
    if (isBuildDiffMethods) {
      const apiMethods = diffData?.testOptimizationMethods;
      if (apiMethods?.length) {
        return mapTestOptimizationMethodsToRows(apiMethods);
      }
      return [];
    }
    return flattenCallables(payload?.entitiesByFile ?? {});
  }, [isBuildDiffMethods, payload?.entitiesByFile, diffData]);

  const counts = useMemo(
    () =>
      countByFilterWithDiffSummary(
        allRows,
        isBuildDiffMethods ? diffData?.testOptimizationSummary : undefined,
      ),
    [allRows, isBuildDiffMethods, diffData?.testOptimizationSummary],
  );
  const filteredRows = useMemo(
    () => filterMethods(allRows, filter, search),
    [allRows, filter, search],
  );

  const focusedRow =
    filteredRows.find(r => r.entityKey === focusedKey) ??
    filteredRows[0] ??
    null;

  const savableRows = useMemo(
    () => filteredRows.filter(isMethodRowSavable),
    [filteredRows],
  );

  const toggleSelect = (row: MethodRow) => {
    if (!isMethodRowSavable(row)) return;
    setSelectedKeys(prev => {
      const next = new Set(prev);
      if (next.has(row.entityKey)) next.delete(row.entityKey);
      else next.add(row.entityKey);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (savableRows.length === 0) return;
    const savableKeys = new Set(savableRows.map(r => r.entityKey));
    const allSavableSelected = savableRows.every(r => selectedKeys.has(r.entityKey));
    if (allSavableSelected) {
      setSelectedKeys(new Set());
    } else {
      setSelectedKeys(savableKeys);
    }
  };

  const selectedRows = allRows.filter(r => selectedKeys.has(r.entityKey));
  const uncoveredCount = selectedRows.filter(
    r => r.coveredBy.length === 0 && r.severity !== 'COVERED',
  ).length;
  const coveredCount = selectedRows.length - uncoveredCount;

  if (loading) {
    return <p className={testOptPlaceholderClass}>Loading methods…</p>;
  }

  if (errorMessage) {
    return (
      <p className={testOptPlaceholderErrorClass}>
        {errorMessage}
      </p>
    );
  }

  if (!allRows.length) {
    return (
      <div className={testOptPlaceholderClass}>
        {isBuildDiffMethods ? (
          <>
            <p>
              {diffData?.scanB?.scanId
                ? (diffData.files?.length ?? 0) > 0
                  ? 'Files changed in this comparison, but none are parsed TypeScript/JavaScript sources (e.g. only Dockerfile or package.json). Methods appear only for .ts/.tsx/.js/.jsx changes.'
                  : 'No added, modified, or deleted methods/functions in this build comparison.'
                : 'Build B scanId was not resolved from the selected build. Run build processing so scanCoverageTimeline has this buildId, then Generate Comparison Report again.'}
            </p>
            {diffData?.scanB?.scanId && (
              <p className="mt-2 text-[0.8rem] text-slate-500">
                Build-to scan: <code>{diffData.scanB.scanId}</code>
              </p>
            )}
            {!diffData?.testOptimizationMethods && diffData?.scanB?.scanId && (
              <p className="mt-2 text-[0.8rem] text-slate-500">
                Compare response is missing <code>testOptimizationMethods</code> — restart
                the backend and re-run the comparison.
              </p>
            )}
          </>
        ) : (
          <p>No callable methods found for this scan. Run scan-session processing first.</p>
        )}
      </div>
    );
  }

  const showChangeFilters =
    isBuildDiffMethods || allRows.some(r => r.changeType);

  const changeFilterButtons: { key: 'added' | 'modified' | 'deleted'; label: string }[] = [
    { key: 'added', label: 'Added' },
    { key: 'modified', label: 'Modified' },
    { key: 'deleted', label: 'Deleted' },
  ];

  const severityFilterButtons: { key: FilterKey; label: string }[] = [
    { key: 'all', label: 'All' },
    { key: 'critical', label: 'Critical' },
    { key: 'important', label: 'Important' },
    { key: 'low', label: 'Low' },
    { key: 'covered', label: 'Covered' },
    { key: 'orphans', label: 'Orphans' },
    { key: 'leaves', label: 'Leaves' },
  ];

  const selectedMethodRows = allRows.filter(
    r => selectedKeys.has(r.entityKey) && isMethodRowSavable(r),
  );

  return (
    <div
      className={cn(testOptMethodsClass, embedded && testOptMethodsEmbeddedClass)}>
      {isBuildDiffMethods && (
        <p className={testOptMethodsSourceHintClass}>
          Build diff methods
          {diffData?.scanB?.buildNumber
            ? ` · Build B ${diffData.scanB.buildNumber}`
            : ''}
          {diffData?.scanB?.scanId ? (
            <>
              {' '}
              · scan <code>{diffData.scanB.scanId}</code>
            </>
          ) : null}
          {diffData?.testOptimizationSummary && (
            <>
              {' '}
              · {diffData.testOptimizationSummary.diffEntityCount} diff{' '}
              {diffData.testOptimizationSummary.diffEntityCount === 1
                ? 'entry'
                : 'entries'}
              {diffData.testOptimizationSummary.methodsRowCount !==
                diffData.testOptimizationSummary.diffEntityCount && (
                  <>
                    {' '}
                    → {diffData.testOptimizationSummary.methodsRowCount} rows
                  </>
                )}
            </>
          )}
        </p>
      )}
      <SaveTestRunSpecModal
        visible={saveSpecOpen}
        buildId={diffData?.scanB?.buildId}
        fromBuildId={diffData?.scanA?.buildId}
        scanId={scanId}
        selectedMethods={selectedMethodRows}
        entitiesByFile={payload?.entitiesByFile}
        onClose={() => setSaveSpecOpen(false)}
        onSaved={specName => {
          setSaveSuccessMessage(`Saved "${specName}" successfully.`);
          setSelectedKeys(new Set());
          onSpecSaved?.();
        }}
      />
      <div className={testOptMethodsToolbarClass}>
        <div className={testOptMethodsToolbarFiltersClass}>
          {showChangeFilters ? (
            <div
              className={testOptMethodsFilterGroupClass}
              role="group"
              aria-label="Change type">
              {changeFilterButtons.map(({ key, label }) => {
                const active = filter === key;
                return (
                  <Button
                    key={key}
                    type="button"
                    variant="ghost"
                    size="sm"
                    className={testOptFilterClassName(active, key)}
                    aria-pressed={active}
                    onClick={() => setFilter(key)}>
                    <span>{label}</span>
                    <span className={testOptFilterCountBadge(active, key)}>
                      {counts[key]}
                    </span>
                  </Button>
                );
              })}
            </div>
          ) : null}
          <div
            className={testOptMethodsFilterGroupClass}
            role="group"
            aria-label="Method filters">
            {severityFilterButtons.map(({ key, label }) => {
              const active = filter === key;
              return (
                <Button
                  key={key}
                  type="button"
                  variant="ghost"
                  size="sm"
                  className={testOptFilterClassName(active)}
                  aria-pressed={active}
                  onClick={() => setFilter(key)}>
                  <span>{label}</span>
                  <span className={testOptFilterCountBadge(active)}>
                    {counts[key]}
                  </span>
                </Button>
              );
            })}
          </div>
        </div>
        <div className={testOptMethodsSearchWrapClass}>
          <Search className={testOptMethodsSearchIconClass} aria-hidden />
          <input
            type="search"
            className={testOptMethodsSearchClass}
            placeholder="Search methods…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            aria-label="Search methods"
          />
        </div>
      </div>

      <div className={testOptMethodsMainClass}>
        <div className={testOptMethodsTableWrapClass}>
          <table className={testOptTableClass}>
            <colgroup>
              <col className={testOptColCheckClass} />
              <col className={testOptColMethodClass} />
              <col className={testOptColFileClass} />
              <col className={testOptColChangeClass} />
              <col className={testOptColSeverityClass} />
              <col className={testOptColScoreClass} />
              <col className={testOptColFanClass} />
              <col className={testOptColFanClass} />
              <col className={testOptColCoveredClass} />
            </colgroup>
            <thead>
              <tr>
                <th className={testOptThCheckClass}>
                  <input
                    type="checkbox"
                    checked={
                      savableRows.length > 0 &&
                      savableRows.every(r => selectedKeys.has(r.entityKey))
                    }
                    disabled={savableRows.length === 0}
                    onChange={toggleSelectAll}
                    aria-label="Select all saveable methods"
                  />
                </th>
                <th className={testOptThClass}>Method / Function</th>
                <th className={testOptThClass}>File</th>
                <th className={testOptThClass}>Change</th>
                <th className={testOptThClass}>Severity</th>
                <th className={testOptThNumClass}>Score</th>
                <th className={testOptThNumClass}>Fan-In</th>
                <th className={testOptThNumClass}>Fan-Out</th>
                <th className={testOptThClass}>Covered By</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.map(row => (
                <MethodTableRow
                  key={row.entityKey}
                  row={row}
                  focused={focusedRow?.entityKey === row.entityKey}
                  selected={selectedKeys.has(row.entityKey)}
                  onFocus={() => setFocusedKey(row.entityKey)}
                  onToggleSelect={() => toggleSelect(row)}
                />
              ))}
            </tbody>
          </table>
        </div>

        {/* {focusedRow && (
          <MethodDetailPanel
            row={focusedRow}
            scanId={scanId}
            detailTab={detailTab}
            onDetailTabChange={setDetailTab}
          />
        )} */}
      </div>

      <div className={testOptFooterClass}>
        <div className={testOptFooterSummaryClass}>
          {saveSuccessMessage ? (
            <span className="text-green-700">{saveSuccessMessage}</span>
          ) : selectedKeys.size > 0 ? (
            <>
              <strong>{selectedKeys.size}</strong> methods selected ·{' '}
              <strong>{uncoveredCount}</strong> without coverage ·{' '}
              <strong>{coveredCount}</strong> covered
            </>
          ) : (
            <>Select methods to create a test run spec</>
          )}
        </div>
        <div className={testOptFooterActionsClass}>
          <Button
            variant="secondary"
            disabled={selectedKeys.size === 0}
            onClick={() => setSelectedKeys(new Set())}>
            Clear selection
          </Button>
          <Button
            disabled={selectedKeys.size === 0}
            onClick={() => {
              setSaveSuccessMessage(null);
              setSaveSpecOpen(true);
            }}>
            Save as Test Run Spec
          </Button>
        </div>
      </div>
    </div>
  );
};

function CoveredByCell({ tests }: { tests: string[] }) {
  if (!tests.length) {
    return <span className={testOptCoveredEmptyClass}>—</span>;
  }
  return (
    <ul className={testOptCoveredListClass} aria-label="Covered by test cases">
      {tests.map(tc => (
        <li key={tc} className={testOptCoveredChipClass} title={tc}>
          {tc}
        </li>
      ))}
    </ul>
  );
}

function MethodTableRow({
  row,
  focused,
  selected,
  onFocus,
  onToggleSelect,
}: {
  row: MethodRow;
  focused: boolean;
  selected: boolean;
  onFocus: () => void;
  onToggleSelect: () => void;
}) {
  const savable = isMethodRowSavable(row);
  return (
    <tr
      className={testOptTableRowClass(focused)}
      onClick={onFocus}>
      <td className={testOptTdCheckClass} onClick={e => e.stopPropagation()}>
        <input
          type="checkbox"
          checked={selected}
          disabled={!savable}
          onChange={onToggleSelect}
          aria-label={`Select ${row.name}`}
          title={
            savable
              ? undefined
              : 'Not linked to scan entities — cannot be saved in a test run spec'
          }
        />
      </td>
      <td className={cn(testOptTdClass, testOptTableMethodClass)}>
        <strong>
          {row.name}()
          {row.lineStart > 0 && (
            <span className={testOptTableMethodLineClass}> L{row.lineStart}</span>
          )}
        </strong>
        <span className={testOptTableMethodTypeClass}>{row.type}</span>
      </td>
      <td className={cn(testOptTdClass, testOptTableFileClass)} title={row.filePath}>
        <span className={testOptTableFileNameClass}>
          {row.filePath.split('/').pop() || row.filePath}
        </span>
        <span className={testOptTableFilePathClass}>{row.filePath}</span>
      </td>
      <td className={testOptTdClass}>
        {row.changeType ? (
          <span className={testOptChangeClass(row.changeType)}>
            {row.changeType.toUpperCase()}
          </span>
        ) : (
          '—'
        )}
      </td>
      <td className={testOptTdClass}>
        <span className={testOptSeverityClass(row.severity)}>
          {row.severity}
        </span>
      </td>
      <td className={testOptTdNumClass}>{row.score}</td>
      <td className={testOptTdNumClass}>{row.fanIn}</td>
      <td className={testOptTdNumClass}>{row.fanOut}</td>
      <td className={cn(testOptTdClass, testOptTableCoveredClass)}>
        <CoveredByCell tests={row.coveredBy} />
      </td>
    </tr>
  );
}

function MethodDetailPanel({
  row,
  scanId,
  detailTab,
  onDetailTabChange,
}: {
  row: MethodRow;
  scanId: string;
  detailTab: 'tests' | 'callers';
  onDetailTabChange: (t: 'tests' | 'callers') => void;
}) {
  const fanIns = fanInKeys(row.entity);
  const fanOuts =
    row.entity.metadata?.resolvedFanout ??
    row.entity.metadata?.resolvedFanouts ??
    [];

  const handleDevRefDownload = () => {
    const csv = buildMethodDevRefCsv(row, scanId);
    const fileBase =
      row.filePath.split('/').pop()?.replace(/\.[^.]+$/, '') ?? 'method';
    const fileName = sanitizeFileNamePart(
      `dev-ref_${fileBase}_${row.name}_${row.lineStart}`,
    );
    downloadCsvContent(csv, fileName);
  };

  return (
    <aside className={testOptDetailClass}>
      <div className={testOptDetailHeaderClass}>
        <p className={testOptDetailTitleClass}>METHOD DETAIL</p>
        <p className={testOptDetailNameClass}>
          {row.name}()
          {row.lineStart > 0 ? ` · line ${row.lineStart}` : ''}
        </p>
        <p className={testOptDetailPathClass}>{row.filePath}</p>
        {row.changeType && (
          <p className={testOptDetailChangeClass}>
            Change:{' '}
            <span className={testOptChangeClass(row.changeType)}>
              {row.changeType.toUpperCase()}
            </span>
          </p>
        )}
      </div>
      <div className={testOptDetailMetricsClass}>
        <div>
          <div className="text-slate-400">Severity</div>
          <span className={testOptSeverityClass(row.severity)}>
            {row.severity}
          </span>
        </div>
        <div>
          <div className="text-slate-400">Score</div>
          <strong>{row.score}</strong>
        </div>
        <div>
          <div className="text-slate-400">Fan-In</div>
          <strong>{row.fanIn}</strong>
        </div>
        <div>
          <div className="text-slate-400">Fan-Out</div>
          <strong>{row.fanOut}</strong>
        </div>
      </div>
      <div className={testOptDetailSubtabsClass}>
        <Button
          type="button"
          variant="ghost"
          className={testOptDetailSubtab(detailTab === 'tests')}
          onClick={() => onDetailTabChange('tests')}>
          Tests
        </Button>
        <Button
          type="button"
          variant="ghost"
          className={testOptDetailSubtab(detailTab === 'callers')}
          onClick={() => onDetailTabChange('callers')}>
          Callers / Callees
        </Button>
      </div>
      <div className={testOptDetailBodyClass}>
        {detailTab === 'tests' ? (
          <>
            <p className="mb-2 mt-0 font-semibold">
              TESTS COVERING THIS METHOD
            </p>
            {row.coveredBy.length === 0 ? (
              <p className="text-slate-400">No linked test cases yet.</p>
            ) : (
              <ul className="m-0 pl-4">
                {row.coveredBy.map(tc => (
                  <li key={tc}>{tc}</li>
                ))}
              </ul>
            )}
            <p className="mb-2 mt-4 font-semibold">
              SUGGESTED TESTS
            </p>
            <p className="text-slate-400">—</p>
          </>
        ) : (
          <>
            <p className="mb-1 mt-0 font-semibold">Fan-In</p>
            {fanIns.length === 0 ? (
              <p className="text-slate-400">—</p>
            ) : (
              <ul className="mb-3 mt-0 pl-4">
                {fanIns.map(k => (
                  <li key={k}>{k.split('_').pop() ?? k}()</li>
                ))}
              </ul>
            )}
            <p className="mb-1 mt-0 font-semibold">Fan-Out</p>
            {fanOuts.length === 0 ? (
              <p className="text-slate-400">—</p>
            ) : (
              <ul className="m-0 pl-4">
                {fanOuts.map(k => (
                  <li key={String(k)}>{String(k).split('_').pop()}()</li>
                ))}
              </ul>
            )}
          </>
        )}
      </div>
      <div className="border-t border-slate-200 px-4 py-3">
        <div className="mb-2">
          <Button variant="secondary">+ Select for Spec</Button>
        </div>
        <Button onClick={handleDevRefDownload}>
          Dev Ref
        </Button>
      </div>
    </aside>
  );
}

export default MethodsTab;
